import { createContext } from '@chakra-ui/react-utils';

import constants from './constants';

// a utility function that checks if a row object can be selected based on its selection being hidden or not.
// NOTE: Disabled rows are currently still allowed; meaning parents can set the value of a disabled row and count towards mixed selected state.
// Preventing selecting disabled rows here would also mean they wouldn't count for mixed state - which is undesirable behavior.
// Allowing them to be selected by parents AND be part of mixed state is a compromise; ideally, we want them to be a part of mixed state but be un-selectable by parents.
const selectingObjectAllowed = (dataObject) => {
  return (
    dataObject.selection == null ||
    (dataObject.selection != constants.selection.hidden &&
      dataObject.selection != constants.selection.disabled)
  );
};

// Get Data Object From Key:
// a function that pulls the dataObject with the input key from our data array.
const getDataObjectFromKey = (dataArray, key) => {
  // Recursively iterate through every object of input dataArray;
  for (const dataObject of dataArray) {
    // If the object's key is our search key...
    if (dataObject.key === key) {
      // ...we have our match, so store it and return to exit the function.
      return dataObject;
    }
    // If the object's key isn't our search key but we have children,
    else if (dataObject.children != null) {
      // Re-run this function with the children as the target and return the result if successful.
      const result = getDataObjectFromKey(dataObject.children, key);
      if (result != null) {
        return result;
      }
    }
  }
};

/**
 * Get Nested Child Keys From Data Object:
 * takes in a data object that may have children,
 * and return an array of string keys for this object and all the contained children.
 *
 * @deprecated use TableV2 and its subcomponents from '@frontend/components' instead.
 */
export const getNestedChildKeysFromDataObject = (dataObject) => {
  // Create a results store;
  let result = selectingObjectAllowed(dataObject) ? [dataObject.key] : [];

  // If we have children,
  if (dataObject.children != null) {
    // Iterate through children recursively and add their keys to store.
    dataObject.children.forEach((child) => {
      // If the child has children,
      if (child.children != null) {
        // Spread the recursive run of this function
        // (which includes this child's key) to the results store.
        result = [...result, ...getNestedChildKeysFromDataObject(child)];
      }
      // If this child doesn't have children,
      else {
        // Push the child's key to the result store.
        if (selectingObjectAllowed(child)) {
          result.push(child.key);
        }
      }
    });
  }
  return result;
};

/**
 * a function that triggers when any parent checkbox in the table is clicked.
 * It works recursively on the nested data; but it will store checked items flat.
 *
 * @deprecated use TableV2 and its subcomponents from '@frontend/components' instead.
 */
export const selectChildren = (
  data,
  key,
  mixed,
  selected,
  onSelect,
  isMainParent,
) => {
  // Retrieve the keys of all children.
  // If this is the main parent for the table...
  // Skip retrieving the data object from the key and use the data as children instead.
  const keysToApply = getNestedChildKeysFromDataObject(
    !isMainParent
      ? getDataObjectFromKey(data, key)
      : { key: '_all', children: data },
  );

  // If this key is in selected and we're not mixed (meaning this and all of its children are in selected.)
  if (selected.includes(key) && !mixed) {
    // Create an array based on selected where this key and all of its children's keys are removed.
    onSelect(selected.filter((el) => !keysToApply.includes(el)));
  }
  // Otherwise, if the key isn't in selected or if it is but it's mixed,
  else {
    // filter keysToApply into only the unique keys not already found in selected...
    // ...so we don't create any duplicates as we add them to selected.
    onSelect([
      ...selected,
      ...keysToApply.filter((el) => !selected.includes(el)),
    ]);
  }
};

/**
 * a function that selects or de-selects a range for shift clicking.
 *
 * @deprecated use TableV2 and its subcomponents from '@frontend/components' instead.
 */
export const selectRange = (
  data,
  selected,
  onSelect,
  lastSelected,
  record,
  mixed,
  isMainParent,
) => {
  const parentDataObject = {
    key: '_all',
    children: data,
  };

  // Get all keys on the current page (in top-down order) so we can select a range from within them.
  const allKeys = getNestedChildKeysFromDataObject(parentDataObject);

  const lastSelectedIndex = allKeys.indexOf(lastSelected);
  const justSelectedIndex = allKeys.indexOf(record.key);
  const selectingUpward = lastSelectedIndex >= justSelectedIndex;

  const keysInRange = selectingUpward
    ? allKeys.slice(justSelectedIndex, lastSelectedIndex + 1)
    : allKeys.slice(lastSelectedIndex, justSelectedIndex + 1);

  // if this key is already selected, de-deselect all of the range.
  // ... and not removing parents, trusting that they'll be removed by their own useEffect child checks.
  if (selected.includes(record.key)) {
    onSelect(
      selected.filter(
        (key) =>
          !keysInRange.includes(key) &&
          !getDataObjectFromKey([parentDataObject], key).children != null,
      ),
    );
  }
  // else if the checkbox just clicked, currently isn't selected,
  else {
    // Select the whole range...
    // ...adding new elements, no duplicates...
    // ... and not adding parents, trusting that they'll be added by their own useEffect child checks.

    onSelect([
      ...selected,
      ...keysInRange.filter(
        (key) =>
          !selected.includes(key) &&
          !getDataObjectFromKey([parentDataObject], key).children != null,
      ),
    ]);
  }

  if (!selectingUpward && record.children != null) {
    selectChildren(data, record.key, mixed, selected, onSelect, isMainParent);
  }
};

/**
 * @deprecated use TableV2 and its subcomponents from '@frontend/components' instead.
 */
const [SelectionProvider, useSelectionContext] = createContext({
  name: 'Select Provider',
  strict: false,
  errorMessage:
    'useSelectionProvider: `context` is undefined. Seems you used a table selection component outside of a table selection wrapper.',
});

export { SelectionProvider, useSelectionContext };
